/**
 * Section 3: Utilities
 *
 * Two variants:
 *  - default (Pre-Walk / QC Lightweight): a single "are all utilities on?" question.
 *  - perUtility (QC Standard v2): electricity / water / gas each asked individually,
 *    with a description of how the agent verified the status.
 */
/* eslint-disable react/jsx-no-bind */

import { useCallback } from 'preact/hooks';
import { useForm } from '@ox/visits-ui/context/FormContext';
import { Select, Textarea } from '@ox/visits-ui/components/shared/FormComponents';

interface Section3UtilitiesProps {
  /** QC Standard v2 variant: ask electricity/water/gas individually. */
  perUtility?: boolean;
}

// Electricity and water are assumed present — ask status directly.
// Gas is handled separately (does the home have it → then status).
const SIMPLE_UTILITIES = [
  { key: 'electricity', label: 'electricity', question: 'Is the electricity on and functional?', example: 'Example: turned on a light switch' },
  { key: 'water', label: 'water', question: 'Is the water on and functional?', example: 'Example: ran a faucet' },
] as const;

export function Section3Utilities({ perUtility }: Section3UtilitiesProps) {
  const { formData, updateField } = useForm();

  const handleUtilitiesFunctionalChange = useCallback((value: string) => {
    updateField('utilities_functional', value);
  }, [updateField]);

  const handleUtilityDetailsChange = useCallback((value: string) => {
    updateField('utility_details', value);
  }, [updateField]);

  const handleFieldChange = useCallback(
    (field: string, value: string) => updateField(field, value),
    [updateField],
  );

  if (perUtility) {
    return (
      <div className="form-section">
        <h2>Utilities</h2>

        {SIMPLE_UTILITIES.map(({ key, label, question, example }) => (
          <div className="form-field" key={key}>
            <Select
              label={question}
              name={`${key}_on_functional`}
              value={formData[`${key}_on_functional`] || ''}
              onChange={(value) => handleFieldChange(`${key}_on_functional`, value)}
              options={[
                { label: 'Select...', value: '', disabled: true },
                { label: 'Yes', value: 'Yes' },
                { label: 'No', value: 'No' },
              ]}
              required
            />
            <Textarea
              label={`Describe how you verified the status of the ${label}`}
              name={`${key}_verification`}
              value={formData[`${key}_verification`] || ''}
              onChange={(value) => handleFieldChange(`${key}_verification`, value)}
              hintMessage={example}
              autoSize
              required
            />
          </div>
        ))}

        {/* Gas: existence gate, then status + verification only if present. */}
        <div className="form-field">
          <Select
            label="Does the home utilize gas?"
            name="has_gas"
            value={formData.has_gas || ''}
            onChange={(value) => handleFieldChange('has_gas', value)}
            options={[
              { label: 'Select...', value: '', disabled: true },
              { label: 'Yes', value: 'Yes' },
              { label: 'No', value: 'No' },
            ]}
            required
          />
          {formData.has_gas === 'Yes' && (
            <>
              <Select
                label="Is the gas on and functional?"
                name="gas_on_functional"
                value={formData.gas_on_functional || ''}
                onChange={(value) => handleFieldChange('gas_on_functional', value)}
                options={[
                  { label: 'Select...', value: '', disabled: true },
                  { label: 'Yes', value: 'Yes' },
                  { label: 'No', value: 'No' },
                ]}
                required
              />
              <Textarea
                label="Describe how you verified the status of the gas"
                name="gas_verification"
                value={formData.gas_verification || ''}
                onChange={(value) => handleFieldChange('gas_verification', value)}
                hintMessage="Example: lit the stove or checked the water heater"
                autoSize
                required
              />
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="form-section">
      <h2>Utilities</h2>

      <Select
        label="Are all utilities (gas, electric, water) on and functioning as intended?"
        name="utilities_functional"
        value={formData.utilities_functional || ''}
        onChange={handleUtilitiesFunctionalChange}
        options={[
          { label: 'Select...', value: '', disabled: true },
          { label: 'Yes', value: 'Yes' },
          { label: 'No', value: 'No' },
          { label: 'Partial', value: 'Partial' },
        ]}
        required
      />

      {(formData.utilities_functional === 'No' || formData.utilities_functional === 'Partial') && (
        <Textarea
          label="Which utilities have issues?"
          name="utility_details"
          value={formData.utility_details || ''}
          onChange={handleUtilityDetailsChange}
          helpText="Be specific about which utilities"
          autoSize
          required
        />
      )}
    </div>
  );
}
